import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Input, Modal } from '@citiguide/ui-components';
import { usePlansStore } from '../../store/plansStore';
import type { SavedPlan } from '../../store/plansStore';
import { useCurationStore } from '../../store/curationStore';
import { buildShareUrl } from './shareUrl';

/** List, open, rename, delete and share saved plans. Everything stays in this browser. */
export default function MyPlansModal() {
  const { t } = useTranslation();
  const isOpen = usePlansStore((s) => s.isModalOpen);
  const close = usePlansStore((s) => s.closeModal);
  const plans = usePlansStore((s) => s.plans);
  const rename = usePlansStore((s) => s.rename);
  const remove = usePlansStore((s) => s.remove);
  const openSavedPlan = useCurationStore((s) => s.openSavedPlan);

  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const open = (plan: SavedPlan) => {
    openSavedPlan(plan);
    close();
  };

  const commitRename = (id: string) => {
    rename(id, draft);
    setRenamingId(null);
  };

  const share = async (plan: SavedPlan) => {
    const url = buildShareUrl(plan);
    setMessage(null);
    try {
      if (typeof navigator.share === 'function') {
        await navigator.share({ title: t('plans.shareTitle', { name: plan.name }), url });
        return;
      }
      await navigator.clipboard.writeText(url);
      setMessage(t('plans.linkCopied'));
    } catch (err) {
      // The visitor dismissing the native share sheet is not an error.
      if ((err as { name?: string })?.name !== 'AbortError') setMessage(t('plans.shareFailed'));
    }
  };

  return (
    <Modal open={isOpen} title={t('plans.title')} onClose={close} size="sm">
      {plans.length === 0 ? (
        <p className="my-plans__empty">{t('plans.empty')}</p>
      ) : (
        <ul className="my-plans__list">
          {[...plans].reverse().map((plan) => (
            <li key={plan.id} className="my-plans__item">
              {renamingId === plan.id ? (
                <Input
                  autoFocus
                  aria-label={t('plans.nameLabel')}
                  value={draft}
                  maxLength={60}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') commitRename(plan.id);
                    if (e.key === 'Escape') {
                      e.stopPropagation();
                      setRenamingId(null);
                    }
                  }}
                />
              ) : (
                <span className="my-plans__name">{plan.name}</span>
              )}
              <div className="my-plans__actions">
                {confirmId === plan.id ? (
                  <>
                    <span>{t('plans.confirmDelete')}</span>
                    <Button
                      size="sm"
                      variant="danger"
                      onClick={() => {
                        remove(plan.id);
                        setConfirmId(null);
                      }}
                    >
                      {t('plans.confirmYes')}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setConfirmId(null)}>
                      {t('plans.cancel')}
                    </Button>
                  </>
                ) : (
                  <>
                    <Button size="sm" onClick={() => open(plan)}>{t('plans.open')}</Button>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => {
                        setDraft(plan.name);
                        setRenamingId(plan.id);
                      }}
                    >
                      {t('plans.rename')}
                    </Button>
                    <Button size="sm" variant="secondary" onClick={() => void share(plan)}>{t('plans.share')}</Button>
                    <Button size="sm" variant="ghost" onClick={() => setConfirmId(plan.id)}>{t('plans.delete')}</Button>
                  </>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
      {message && <p role="status" className="my-plans__message">{message}</p>}
    </Modal>
  );
}
